import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAutosaveQueue — durable answer saving for an exam in progress.
 *
 * This is the single biggest reliability upgrade over the old exam screen,
 * whose save path was `.catch(() => {})` — a dropped save lost an answer and
 * told the candidate nothing. On college wifi during a placement drive that is
 * not a hypothetical.
 *
 * What it guarantees:
 *   1. Every change is queued, not fired-and-forgotten.
 *   2. A failed save retries with backoff instead of vanishing.
 *   3. The queue is mirrored to localStorage, so a browser crash mid-flush
 *      replays on the next mount rather than losing work.
 *   4. The candidate can ALWAYS see the truth — "Saved", "Saving…",
 *      "3 pending", "Offline".
 *   5. Submit blocks until the queue drains, and names what could not be saved.
 *
 * One patch per question is held at a time: a candidate who changes their mind
 * three times needs the LAST answer saved, not all three replayed in order.
 *
 * Built for a full hall on one server: retries carry random jitter, so five
 * hundred browsers that failed together do not retry together; and an answer
 * the server has REFUSED (the exam is over, the question is not on the paper)
 * is dropped rather than retried for the rest of the sitting.
 */

const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000];
const DEBOUNCE_MS = 600;
// Someone typing a long answer without pausing still gets saved this often.
const MAX_WAIT_MS = 5000;
const storageKey = (attemptId) => `vc-exam-queue-${attemptId}`;

/** ±30%, so a hall's retries spread out instead of arriving as one wave. */
const jitter = (ms) => Math.round(ms * (0.7 + Math.random() * 0.6));

/**
 * How the server answered a failed save.
 *   ended    — the attempt is closed or out of time (409 / 410). Nothing will
 *              ever save again; the runner moves on to submitting.
 *   rejected — this one patch can never succeed (400). Retrying it forever
 *              would block submit for an answer the server will not take.
 *   retry    — network, 5xx, 429, anything else.
 */
export function classifySaveError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  if (status === 410 || data.expired || data.errorCode === 'EXPIRED') return 'ended';
  if (status === 409 || data.errorCode === 'ALREADY_SUBMITTED' || data.submitted) return 'ended';
  if (status === 400) return 'rejected';
  return 'retry';
}

export default function useAutosaveQueue({ attemptId, saveAnswer, onExpired, onRejected }) {
  // questionId -> { patch, attempts }
  const queueRef = useRef(new Map());
  const timerRef = useRef(null);
  const retryRef = useRef(null);
  const flushingRef = useRef(false);
  const mountedRef = useRef(true);
  const firstPendingAtRef = useRef(0);
  const endedRef = useRef(false);

  const [pending, setPending] = useState(0);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState([]);
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // ── localStorage mirror ────────────────────────────────────────────
  const persist = useCallback(() => {
    if (!attemptId) return;
    try {
      const entries = [...queueRef.current.entries()].map(([qid, v]) => [qid, v.patch]);
      if (entries.length === 0) window.localStorage.removeItem(storageKey(attemptId));
      else window.localStorage.setItem(storageKey(attemptId), JSON.stringify(entries));
    } catch {
      // Private mode or a full quota. The in-memory queue still works; we just
      // lose crash recovery. Never let this break the exam.
    }
  }, [attemptId]);

  const syncCounts = useCallback(() => {
    if (!mountedRef.current) return;
    setPending(queueRef.current.size);
  }, []);

  // ── flush ──────────────────────────────────────────────────────────
  const flush = useCallback(async () => {
    if (flushingRef.current || !attemptId) return;
    if (queueRef.current.size === 0) return;

    flushingRef.current = true;
    firstPendingAtRef.current = 0;
    if (mountedRef.current) setSaving(true);

    let backoffNeeded = false;

    // Snapshot the keys: a save that lands while we are flushing must be picked
    // up by the NEXT pass, not skipped because we iterated a live map.
    for (const questionId of [...queueRef.current.keys()]) {
      const entry = queueRef.current.get(questionId);
      if (!entry) continue;

      try {
        const result = await saveAnswer(questionId, entry.patch);
        // The answer landed — drop it even if a newer patch arrived meanwhile,
        // as long as it is the same object we sent.
        if (queueRef.current.get(questionId) === entry) {
          queueRef.current.delete(questionId);
        }
        if (mountedRef.current) {
          setLastSavedAt(Date.now());
          setFailed((f) => (f.length ? f.filter((id) => id !== questionId) : f));
        }
        if (result?.expired) {
          queueRef.current.clear();
          if (!endedRef.current) {
            endedRef.current = true;
            onExpired?.(result);
          }
          break;
        }
      } catch (err) {
        const kind = classifySaveError(err);
        if (kind === 'ended') {
          // The attempt is over. Drop everything — none of it can land — and
          // let the runner submit, which the server answers idempotently.
          queueRef.current.clear();
          if (mountedRef.current) setFailed([]);
          if (!endedRef.current) {
            endedRef.current = true;
            onExpired?.(err?.response?.data || { expired: true });
          }
          break;
        }
        if (kind === 'rejected') {
          if (queueRef.current.get(questionId) === entry) queueRef.current.delete(questionId);
          if (mountedRef.current) setFailed((f) => f.filter((id) => id !== questionId));
          onRejected?.(questionId, err?.response?.data?.message, err?.response?.data);
          continue;
        }
        entry.attempts = (entry.attempts || 0) + 1;
        backoffNeeded = true;
        if (mountedRef.current) {
          setFailed((f) => (f.includes(questionId) ? f : [...f, questionId]));
        }
      }
    }

    persist();
    syncCounts();
    flushingRef.current = false;
    if (mountedRef.current) setSaving(false);

    // Retry whatever is left, with backoff driven by the most-retried entry.
    if (backoffNeeded && queueRef.current.size > 0) {
      const worst = Math.max(
        0,
        ...[...queueRef.current.values()].map((e) => e.attempts || 0)
      );
      const delay = BACKOFF_MS[Math.min(worst - 1, BACKOFF_MS.length - 1)] || 1000;
      clearTimeout(retryRef.current);
      retryRef.current = setTimeout(() => flush(), jitter(delay));
    }
  }, [attemptId, saveAnswer, onExpired, onRejected, persist, syncCounts]);

  // ── enqueue ────────────────────────────────────────────────────────
  const enqueue = useCallback(
    (questionId, patch) => {
      if (!questionId || endedRef.current) return;
      const existing = queueRef.current.get(questionId);
      // Merge, so flagging a question does not discard the answer typed into it.
      queueRef.current.set(questionId, {
        patch: { ...(existing?.patch || {}), ...patch },
        attempts: 0,
      });
      persist();
      syncCounts();

      const now = Date.now();
      if (!firstPendingAtRef.current) firstPendingAtRef.current = now;
      clearTimeout(timerRef.current);
      // Debounced, but never starved: continuous typing still saves every few seconds.
      const waited = now - firstPendingAtRef.current;
      timerRef.current = setTimeout(() => flush(), waited >= MAX_WAIT_MS ? 0 : DEBOUNCE_MS);
    },
    [flush, persist, syncCounts]
  );

  /**
   * Drain everything now. Used before submit.
   * Returns { ok, failedQuestionIds } so the caller can name what is at risk
   * rather than submitting silently over the top of it.
   */
  const flushNow = useCallback(async () => {
    clearTimeout(timerRef.current);
    clearTimeout(retryRef.current);

    for (let i = 0; i < 3 && queueRef.current.size > 0; i++) {
      // eslint-disable-next-line no-await-in-loop
      await flush();
      if (queueRef.current.size === 0) break;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 800));
    }
    return {
      ok: queueRef.current.size === 0,
      failedQuestionIds: [...queueRef.current.keys()],
    };
  }, [flush]);

  // ── crash recovery ─────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    if (!attemptId) return undefined;
    try {
      const raw = window.localStorage.getItem(storageKey(attemptId));
      if (raw) {
        const entries = JSON.parse(raw);
        if (Array.isArray(entries) && entries.length) {
          for (const [qid, patch] of entries) {
            queueRef.current.set(qid, { patch, attempts: 0 });
          }
          syncCounts();
          // Replay before the candidate touches anything.
          flush();
        }
      }
    } catch {
      // A corrupt mirror is not worth failing over; drop it.
      try { window.localStorage.removeItem(storageKey(attemptId)); } catch { /* noop */ }
    }
    return () => { mountedRef.current = false; };
    // Deliberately runs once per attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  // ── connectivity ───────────────────────────────────────────────────
  useEffect(() => {
    const up = () => {
      setOnline(true);
      // Coming back online is the best possible moment to retry.
      if (queueRef.current.size > 0) flush();
    };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, [flush]);

  useEffect(() => () => {
    mountedRef.current = false;
    clearTimeout(timerRef.current);
    clearTimeout(retryRef.current);
  }, []);

  /** Everything is safely on the server and nothing is waiting. */
  const clean = pending === 0 && !saving;

  /** One object the footer renders directly — no status logic in the view. */
  // "Not saved — retrying" only once a save has actually FAILED. An answer
  // waiting out the debounce is just saving; warning about it would flash amber
  // under the title after every click.
  const status = (() => {
    if (!online) return { kind: "offline", text: "Offline — your answers are saved on this device" };
    if (failed.length > 0 && pending > 0) {
      return { kind: "pending", text: `${pending} answer${pending === 1 ? "" : "s"} not saved yet — retrying` };
    }
    if (saving || pending > 0) return { kind: "saving", text: "Saving…" };
    if (lastSavedAt) return { kind: "saved", text: "All answers saved" };
    return { kind: "idle", text: "All answers saved" };
  })();

  return { enqueue, flushNow, pending, saving, failed, online, clean, status, lastSavedAt };
}
